/**
 * Salary Calculator Functions - 2026 Israeli Tax System
 * All calculations happen client-side for maximum privacy and security
 * 
 * Source: Israeli Tax Authority (kolzchut.org.il)
 * Last Updated: January 2026
 */

// ============================================================================
// TAX BRACKETS (מדרגות מס) - 2026
// Source: world.tax-rates.org / Israeli income tax 2026
// ============================================================================
const TAX_BRACKETS_2026 = [
  // מדרגות מס 2026 (שכר שנתי חייב במס)
  // כפי שמופיע ב-README של הפרויקט: 10/14/20/31/35/47/50
  { min: 0,       max: 84120,  rate: 0.10 }, // 10%
  { min: 84121,   max: 120720, rate: 0.14 }, // 14%
  { min: 120721,  max: 193800, rate: 0.20 }, // 20%
  { min: 193801,  max: 269280, rate: 0.31 }, // 31%
  { min: 269281,  max: 560280, rate: 0.35 }, // 35%
  { min: 560281,  max: 721560, rate: 0.47 }, // 47%
  { min: 721561,  max: Infinity, rate: 0.50 } // 50%
];

// ============================================================================
// CONSTANTS
// ============================================================================
const HEALTH_INSURANCE_RATE = 0.031;  // 3.1%
const PENSION_RATE = 0.06;             // 6% employee contribution (2026 standard)

// Study Fund (קרן השתלמות) - 2026
const TRAINING_FUND = {
  rate: 0.025,                        // 2.5% employee contribution (tax-exempt up to ceiling)
  employer_rate: 0.075,               // 7.5% employer contribution
  taxExemptCeilingMonthly: 15712,     // ₪15,712/month = ₪188,544/year ceiling
  taxExemptCeilingAnnual: 188544,
  maxTaxExemptMonthly: 392.80,        // 2.5% of ₪15,712
  maxTaxExemptAnnual: 4713.60
};

// National Insurance & Health Insurance Constants (ביטוח לאומי + ביטוח בריאות) - 2026
// Source: jobcalc.co.il / Bituach Leumi 2026
const NI_HEALTH_BASE_THRESHOLD = 7703;          // ₪7,703/month
const NI_CEILING = 51910;                       // ₪51,910/month
// Employee National Insurance (עובד שכיר)
const NI_BASE_RATE_EMPLOYEE = 0.0104;           // 1.04% up to threshold
const NI_STANDARD_RATE_EMPLOYEE = 0.07;         // 7% above threshold
// Health Insurance (ביטוח בריאות) - employee share
const HEALTH_BASE_RATE = 0.0323;                // 3.23% up to threshold
const HEALTH_STANDARD_RATE = 0.0517;            // 5.17% above threshold

// Tax Credit Constants
const MONTHLY_TAX_CREDIT_VALUE = 242; // Official 2026 value per point per month

// Pension tax credit (זיכוי מס לפנסיה) - based on 2026 commonly referenced limits:
// - benefit is 35% of the eligible monthly pension deposit
// - eligible monthly deposit is capped (for the tax credit) at ₪679
// This matches the delta seen in user payslips where income tax is lower by ~₪237.
const PENSION_TAX_CREDIT_RATE = 0.35;
const PENSION_TAX_DEPOSIT_CEILING_MONTHLY = 679;

// ============================================================================
// CALCULATION FUNCTIONS
// ============================================================================

/**
 * Get the marginal tax rate for a given annual salary
 * @param {number} annualSalary - Annual salary in ₪
 * @returns {number} Tax rate as percentage (0-50)
 */
export function getMarginalTaxRate(annualSalary) {
  for (let bracket of TAX_BRACKETS_2026) {
    if (annualSalary >= bracket.min && annualSalary <= bracket.max) {
      return bracket.rate * 100; // Return as percentage
    }
  }
  return 0;
}

/**
 * Calculate income tax based on 2026 Israeli tax brackets
 * @param {number} monthlySalary - Monthly gross salary in ₪
 * @param {number} taxCreditPoints - Number of tax credit points (default 2.25)
 * @returns {number} Monthly income tax in ₪
 */
export function calculateTax(monthlySalary, taxCreditPoints = 2.25) {
  // Convert monthly salary to annual for bracket calculation
  const annualSalary = monthlySalary * 12;
  let annualTax = 0;

  // Calculate tax based on annual brackets
  for (let bracket of TAX_BRACKETS_2026) {
    const salaryInBracket = Math.min(annualSalary, bracket.max) - bracket.min;
    if (salaryInBracket > 0) {
      annualTax += salaryInBracket * bracket.rate;
    }
    if (annualSalary <= bracket.max) break;
  }

  // Apply tax credit points (official 2026 value: ₪242/month per point)
  const annualCreditAmount = taxCreditPoints * MONTHLY_TAX_CREDIT_VALUE * 12;
  const netAnnualTax = Math.max(0, annualTax - annualCreditAmount);

  // Convert back to monthly tax
  const monthlyTax = Math.round(netAnnualTax / 12);
  return monthlyTax;
}

/**
 * Calculate health insurance (ביטוח בריאות) - employee share
 * 3.23% up to ₪7,703, 5.17% above, capped at ₪51,910
 * @param {number} grossSalary - Monthly gross salary in ₪
 * @returns {number} Monthly health insurance in ₪
 */
export function calculateHealthInsurance(grossSalary) {
  const applicableSalary = Math.min(grossSalary, NI_CEILING);
  const baseAmount = Math.min(applicableSalary, NI_HEALTH_BASE_THRESHOLD);
  const standardAmount = Math.max(0, applicableSalary - NI_HEALTH_BASE_THRESHOLD);
  const insurance =
    baseAmount * HEALTH_BASE_RATE +
    standardAmount * HEALTH_STANDARD_RATE;
  return Math.round(insurance);
}

/**
 * Calculate national insurance (ביטוח לאומי) - employee share
 * Two-tier system: 1.04% up to ₪7,703, then 7% above, capped at ₪51,910
 * @param {number} grossSalary - Monthly gross salary in ₪
 * @returns {number} Monthly national insurance in ₪
 */
export function calculateNationalInsurance(grossSalary) {
  // Cap salary at ceiling
  const applicableSalary = Math.min(grossSalary, NI_CEILING);
  
  let insurance = 0;
  
  // Base rate on salary up to threshold
  const baseAmount = Math.min(applicableSalary, NI_HEALTH_BASE_THRESHOLD);
  insurance += baseAmount * NI_BASE_RATE_EMPLOYEE;
  
  // Standard rate on salary above threshold
  if (applicableSalary > NI_HEALTH_BASE_THRESHOLD) {
    const standardAmount = applicableSalary - NI_HEALTH_BASE_THRESHOLD;
    insurance += standardAmount * NI_STANDARD_RATE_EMPLOYEE;
  }
  
  return Math.round(insurance);
}

/**
 * Calculate pension contribution (פנסיה)
 * @param {number} grossSalary - Monthly gross salary in ₪
 * @param {number} pensionPercentage - Pension percentage (default 6%)
 * @returns {number} Monthly pension contribution in ₪
 */
export function calculatePension(grossSalary, pensionPercentage = 6) {
  return Math.round(grossSalary * (pensionPercentage / 100));
}

/**
 * Calculate study fund (קרן השתלמות)
 * @param {number} grossSalary - Monthly gross salary in ₪
 * @param {boolean} hasTrainingFund - Whether employee has study fund
 * @param {boolean} useFullSalary - false = capped (tax-exempt), true = full salary (taxable above ceiling)
 * @returns {number} Monthly study fund contribution in ₪
 */
export function calculateTrainingFund(grossSalary, hasTrainingFund = true, useFullSalary = false) {
  if (!hasTrainingFund) return 0;

  if (useFullSalary) {
    // Full salary: contributions above ceiling are taxable
    return Math.round(grossSalary * TRAINING_FUND.rate);
  } else {
    // Default/capped: only up to ceiling is tax-exempt
    const applicableSalary = Math.min(grossSalary, TRAINING_FUND.taxExemptCeilingMonthly);
    return Math.round(applicableSalary * TRAINING_FUND.rate);
  }
}

/**
 * Complete salary calculation
 * @param {object} params - Calculation parameters
 * @returns {object} Complete salary breakdown
 */
export function calculateSalary(params) {
  const {
    grossSalary,
    taxCreditPoints = 2.25,
    pensionPercentage = 6,
    taxableAdditions = 0,
    otherDeductions = 0,
    hasTrainingFund = true,
    trainingFundUseFullSalary = false
  } = params;

  // Validate inputs
  if (!grossSalary || grossSalary < 0) {
    throw new Error('Invalid gross salary amount');
  }

  if (taxCreditPoints < 0 || taxCreditPoints > 10) {
    throw new Error('Invalid tax credit points (must be between 0 and 10)');
  }

  // Perform calculations
  const taxableBase = grossSalary + (taxableAdditions || 0);
  let tax = calculateTax(taxableBase, taxCreditPoints);
  const nationalInsurance = calculateNationalInsurance(taxableBase);
  const healthInsurance = calculateHealthInsurance(taxableBase);
  const pension = calculatePension(grossSalary, pensionPercentage);
  const trainingFund = calculateTrainingFund(grossSalary, hasTrainingFund, trainingFundUseFullSalary);

  // Apply pension income-tax credit (directly reduces income tax, not taxable base).
  // Payroll rounding tends to effectively be "round down" here, which matches the
  // consistent delta we see across two payslips.
  const pensionTaxCredit = Math.floor(
    Math.min(pension, PENSION_TAX_DEPOSIT_CEILING_MONTHLY) * PENSION_TAX_CREDIT_RATE
  );
  tax = Math.max(0, tax - pensionTaxCredit);

  const totalDeductions = tax + nationalInsurance + healthInsurance + pension + trainingFund + otherDeductions;
  const netSalary = Math.round(grossSalary - totalDeductions);

  // Determine study fund tax status
  let trainingFundTaxInfo = {
    hasTrainingFund: hasTrainingFund,
    method: trainingFundUseFullSalary ? 'Full Salary (Taxable above ceiling)' : 'Default/Capped (Tax-exempt)',
    amount: trainingFund,
    taxStatus: 'N/A'
  };

  if (hasTrainingFund) {
    if (trainingFundUseFullSalary) {
      const taxExemptAmount = Math.round(TRAINING_FUND.taxExemptCeilingMonthly * TRAINING_FUND.rate);
      const taxableAmount = trainingFund - taxExemptAmount;
      trainingFundTaxInfo.taxStatus = taxableAmount > 0 
        ? `Partially taxable (₪${taxableAmount} taxable above ceiling)` 
        : 'Fully tax-exempt';
    } else {
      trainingFundTaxInfo.taxStatus = 'Fully tax-exempt (capped at ceiling)';
    }
  }

  // Return complete calculation result
  return {
    success: true,
    data: {
      grossSalary: Math.round(grossSalary),
      taxableAdditions: Math.round(taxableAdditions),
      tax,
      nationalInsurance,
      healthInsurance,
      pension,
      trainingFund,
      otherDeductions: Math.round(otherDeductions),
      netSalary,
      breakdown: {
        incomeTax: tax,
        nationalInsurance: nationalInsurance,
        healthInsurance: healthInsurance,
        pensionContribution: pension,
        trainingFund: trainingFund,
        otherDeductions: Math.round(otherDeductions)
      },
      trainingFundInfo: trainingFundTaxInfo,
      effectiveTaxRate: Math.round((tax / grossSalary) * 100 * 10) / 10,
      // "מס שולי" מבוסס על ההכנסה החייבת בפועל (כולל תוספות חייבות במס)
      marginalTaxRate: getMarginalTaxRate(taxableBase * 12),
      taxCreditPoints,
      pensionPercentage,
      hasTrainingFund,
      trainingFundUseFullSalary,
      calculationDate: new Date().toISOString()
    }
  };
}

// ============================================================================
// EXPORT TAX RATES (for informational purposes)
// ============================================================================
export function getTaxRates(year = 2026) {
  if (year !== 2026) {
    throw new Error('Only 2026 rates are currently available');
  }

  return {
    success: true,
    data: {
      year: 2026,
      basicTaxRates: TAX_BRACKETS_2026.map(bracket => ({
        minIncome: bracket.min,
        maxIncome: bracket.max === Infinity ? null : bracket.max,
        rate: bracket.rate * 100
      })),
      healthInsuranceRate: HEALTH_INSURANCE_RATE * 100,
      pensionRate: PENSION_RATE * 100,
      trainingFund: {
        employeeRate: TRAINING_FUND.rate * 100,
        employerRate: TRAINING_FUND.employer_rate * 100,
        taxExemptCeilingMonthly: TRAINING_FUND.taxExemptCeilingMonthly,
        taxExemptCeilingAnnual: TRAINING_FUND.taxExemptCeilingAnnual
      }
    }
  };
}
